import { useState } from 'react';

import PropTypes from 'prop-types';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';
import BasicDialog from '@zenbusiness/zui/BasicDialog';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import useDeleteMfaMethod from '#client/hooks/useDeleteMfaMethod';

import { REMOVAL_BLOCKED_MESSAGES } from '../TwoStepVerification/removalRules';

export const LOCALIZED_TEXT = {
  title: 'dashboard.accountSettings.removeMfaMethod.title',
  body: 'dashboard.accountSettings.removeMfaMethod.body',
  confirm: 'dashboard.accountSettings.removeMfaMethod.confirm',
  cancel: 'dashboard.accountSettings.removeMfaMethod.cancel',
  failedOverline: 'dashboard.accountSettings.removeMfaMethod.failed.overline',
  failedTitle: 'dashboard.accountSettings.removeMfaMethod.failed.title',
  failedBody: 'dashboard.accountSettings.removeMfaMethod.failed.body',
  tryAgain: 'dashboard.accountSettings.removeMfaMethod.tryAgain',
};

const RemoveMfaMethodDialog = ({ isOpen, method, onClose }) => {
  const { formatMessage: fm } = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { deleteMfaMethod, loading } = useDeleteMfaMethod();
  const [refusal, setRefusal] = useState(null);
  const [failed, setFailed] = useState(false);

  const handleClosePressed = useButtonPressedCallback({
    appSection: 'account',
    elementName: 'remove_mfa_method_close',
    elementStyle: 'icon',
  });

  const handleClose = () => {
    setRefusal(null);
    setFailed(false);
    onClose();
  };

  const handleConfirm = async () => {
    setRefusal(null);
    setFailed(false);
    const result = await deleteMfaMethod(method.id);

    // notFound means the method is already gone (e.g. removed from another tab), so a retry
    // can't work; closing refreshes the list to show the current state.
    if (result?.removed || result?.errorCode === 'notFound') {
      handleClose();
      return;
    }

    const refusalMessageId = REMOVAL_BLOCKED_MESSAGES[result?.errorCode];

    if (!refusalMessageId) {
      setFailed(true);
      return;
    }

    // The card blocks these upfront; this covers a refusal the card couldn't predict. Retrying
    // won't help, so the dialog keeps its content and explains why. A refusal from customers-api
    // doesn't say which method is missing, so the copy stays generic ("another method").
    setRefusal(fm({ id: refusalMessageId }, { missing: 'other' }));
  };

  return (
    <BasicDialog
      size="sm"
      overline={failed ? fm({ id: LOCALIZED_TEXT.failedOverline }) : method.label}
      title={
        failed ? fm({ id: LOCALIZED_TEXT.failedTitle }, { method: method.name }) : fm({ id: LOCALIZED_TEXT.title })
      }
      primaryAction={{
        label: fm({ id: failed ? LOCALIZED_TEXT.tryAgain : LOCALIZED_TEXT.confirm }),
        color: failed ? 'primary' : 'error',
        clickstreamProps: {
          appSection: 'account',
          elementName: failed ? 'remove_mfa_method_try_again' : 'remove_mfa_method_confirm',
        },
        // customers-api already refused this removal, so resending it can't succeed.
        disabled: Boolean(refusal),
        loading,
        onClick: handleConfirm,
      }}
      secondaryAction={{
        label: fm({ id: LOCALIZED_TEXT.cancel }),
        clickstreamProps: {
          appSection: 'account',
          elementName: 'remove_mfa_method_cancel',
        },
        onClick: handleClose,
      }}
      onClose={() => {
        handleClose();
        handleClosePressed();
      }}
      open={isOpen}
      fullScreen={fullScreen}
    >
      <Text size="md" color="primary">
        {failed
          ? fm({ id: LOCALIZED_TEXT.failedBody })
          : fm({ id: LOCALIZED_TEXT.body }, { method: method.detail || method.label })}
      </Text>
      {refusal && (
        <Text size="sm" color="red">
          {refusal}
        </Text>
      )}
    </BasicDialog>
  );
};

RemoveMfaMethodDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  method: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    // How the method reads mid-sentence (e.g. "your authenticator app").
    name: PropTypes.string.isRequired,
    // What the user recognizes the method by (e.g. "••• ••• 4567"); falls back to the label.
    detail: PropTypes.string,
  }).isRequired,
  // Called on every close, including after a removal, so the parent can refresh the list.
  onClose: PropTypes.func.isRequired,
};

export default RemoveMfaMethodDialog;
